import _ from "lodash";
import { Modal, Button as AntButton, TableColumnsType, Table, Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { IGetStudentPaymentsDetails, IStudentBreakdown, IStudentPaymentsDetail, PaymentSttCode } from "../../../../../models/payments.model";
import useUtil from "../../../../../hooks/useUtil";
import useAxios from "../../../../../hooks/useAxios";
import { useEffect, useState } from "react";
import { getStudentPaymentsDetailsTodo } from "../../../../../api/payments.api";
import useNotification from "../../../../../hooks/useNotification";
import Checkbox from "../../../../Checkbox";
import { createPartialRefund } from "../../../../../api/refunds.api";
import { IResponse } from "../../../../../models/common.api.model";
import { StudentContents } from "../../../../../pages/student/student";
import ConfirmModal from "../../../../modals/ConfirmModal";
import PartialRefundConfirmModal from "./PartialRefundConfirmModal";

interface IProps {
    visible:boolean, 
    setVisible:(visible: boolean) => void, 
    paymentRecord?: IStudentBreakdown,
    setTabIndex: (tabIndex: number) => void;
    isPlaceTest:boolean
  };

const PartialRefundModal = ({
    visible, 
    setVisible, 
    paymentRecord,
    setTabIndex,
    isPlaceTest } : IProps
) => {
    const { t } = useTranslation();
    const { getCommonCodeLabel } = useUtil();
    const { data: res, isLoading, error, execute } = useAxios<IGetStudentPaymentsDetails>();
    const { data: createRefundRes, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IResponse<String>>();
    const [refundAmounts, setRefundAmounts] = useState<Map<number, number>>(new Map<number, number>());
    const [isRePayment, setIsRePayment] = useState<Map<number, boolean>>(new Map<number, boolean>());
    const { warningNotification, successNotification, errorNotification } = useNotification();
    const [studentMemberCode, setStudentMemberCode] = useState<string>();
    const [studentClassCode, setStudentClassCode] = useState<string>();
    const [confirmOpenVisible, setConfirmOpenVisible] = useState(false);

    let refundTotalAmount : number = 0;

    useEffect(() => {
      if (res && !isLoading) {
        let data: any = _.cloneDeep(res?.data);
        let tempRefundAmount : Map<number, number> = new Map<number, number>();
        let tempStudentCode : string = '';
        let tempClassCode : string = '';

        data?.forEach((item: any, index: number) => {
          item.index = index;
          
          let defaultRefundAmount = 0;
          if(item.actualPaymentAmount - (item.successRefundAmount + item.waitRefundAmount) > 0) {
            defaultRefundAmount = item.actualPaymentAmount - (item.successRefundAmount + item.waitRefundAmount);
          }
          tempRefundAmount.set(item.paymentNo, defaultRefundAmount);

          //환불 학생코드 셋팅
          if(!tempStudentCode) {
            tempStudentCode = item.studentMemberCode;
          }

          //환불 학생 classCode 셋팅
          if(!tempClassCode) {
            tempClassCode = item.classCode;
          }
        });

        setStudentMemberCode(tempStudentCode);
        setStudentClassCode(tempClassCode);
        setRefundAmounts(tempRefundAmount);
      }
    }, [res, isLoading]);

    useEffect(() => {
      if (createRefundRes && !isLoading2) {
        if(createRefundRes.result === "success") {
          successNotification(t('student.student.text33')); 
          setVisible(false);
          setTabIndex(StudentContents.Refund);
        } else {
          errorNotification(t('student.student.text32'));
        }
      }
    }, [createRefundRes, isLoading2])

      
    const onSubmit = () => {
      if(!paymentRecord || !studentMemberCode || (!isPlaceTest && !studentClassCode) || isLoading2) {
        warningNotification(t('student.student.text31'));
        return false;
      }
      
      let resultTotalRefundAmount = 0;
      if(paymentRecord) {
        const req: any = {
          paymentRecordCode : paymentRecord.paymentRecordCode, 
          totalAmount : 0,
          studentClassCode : studentClassCode,
          studentMemberCode : studentMemberCode,
          refundAmountList : []
        };

        refundAmounts.forEach((value, key, map) => {
          resultTotalRefundAmount += value;
          if(value > 0) {
            req.refundAmountList.push({paymentNo : key, refundAmount : value, isRePayment : (isRePayment.get(key) === true) ? true : false});
          }
        });

        if(resultTotalRefundAmount <= 0) {
          return warningNotification(t('student.student.text30'));
        } else {
          req.totalAmount = resultTotalRefundAmount;
          execute2(createPartialRefund(req));
        }
      }
    };

    const onSetPartialRefundAmounts = (paymentNo: number, maxPartialRefundAmount: number, value: string) => {
      let numberValue : number = Number(value);

      if(numberValue > maxPartialRefundAmount) {
        return false;
      }

      let tempRefundAmount : Map<number, number> = new Map(refundAmounts.entries());
      tempRefundAmount.set(paymentNo, numberValue);
      setRefundAmounts(tempRefundAmount);	  
    };

    const onOpenConfirm = () => {
      setConfirmOpenVisible(true);
    };

    const onIsRePaymentSelect = (paymentNo : number) => {
      let tempRefundAmount : Map<number, boolean> = new Map(isRePayment.entries());
      let tempValue : boolean = tempRefundAmount.get(paymentNo) === true ? true : false;

      tempRefundAmount.set(paymentNo, !tempValue);
      setIsRePayment(tempRefundAmount);
    }

    const getPaymentSttCode = (paymentSttCode: PaymentSttCode) => {
      switch (paymentSttCode) {
        case 'PA':
          return t('student.student.column77');
        case 'CO':
          return t('student.student.column78');
        case 'WA':
          return t('student.student.column79');
        case 'CA':
          return t('student.student.column80');
      }
    };

    const columns: TableColumnsType<IStudentPaymentsDetail> = [
      {
        title: t('student.student.column65'),
        dataIndex: 'paymentTargetCode',
        render: value => getCommonCodeLabel('PA0001', value),
      },
      {
        title: t('student.student.column22'),
        dataIndex: 'semesterName',
      },
      {
        title: t('common.menu.class'),
        dataIndex: 'className',
      },
      {
        title: t('student.student.column48'),
        dataIndex: 'monthNameCode',
      },
      {
        title: t('student.student.column81'),
        dataIndex: 'price',
        render: (value: number) => value?.toLocaleString(),
      },
      {
        title: t('student.student.column39'),
        dataIndex: 'discountAmount',
        render: (value: number) => value?.toLocaleString(),
      },
      {
        title: t('student.student.column40'),
        render: value => (value.price - value.discountAmount)?.toLocaleString(),
      },
      {
        title: t('student.student.column4'),
        dataIndex: 'paymentSttCode',
        render: (value, record) => {
          if (record.paymentRecordStatus === 'OK') {
            return getPaymentSttCode(value);
          } else {
            return getCommonCodeLabel('PA0012', record.paymentRecordStatus);
          }
        },
      },
      {
        title: t('class.class.column46'),
        dataIndex: 'successRefundAmount',
        render: (value: number) => value?.toLocaleString(),
      },
      {
        title: t('student.student.column144'),
        render: (record) => record.actualPaymentAmount - (record.successRefundAmount + record.waitRefundAmount),
      },
      {
        title: t('student.student.column108'),
        align: 'center',
        render: (record) => {
            return <>
                <input 
                    type="number"
                    className="form-control w-100p"
                    value={refundAmounts.get(record.paymentNo)}
                    style={{color:'red'}}
                    onChange={(e) => onSetPartialRefundAmounts(record.paymentNo, record.actualPaymentAmount - (record.successRefundAmount + record.waitRefundAmount), e.target.value)}
                    disabled={(record.actualPaymentAmount - (record.successRefundAmount + record.waitRefundAmount)) <= 0 ? true : false}
                />
            </>
        }
      },
      {
        title: t('student.student.column145'),
        align: 'center',
        render: (record) => {
          return <>
            {
              (record.paymentTargetCode === 'TU' || record.paymentTargetCode === 'ST' || record.paymentTargetCode === 'ON' || record.paymentTargetCode === 'AL') &&
              <Checkbox
                checked={(isRePayment.get(record.paymentNo)) === true?true:false}
                onChange={() => onIsRePaymentSelect(record.paymentNo)}
                text=""
                disabled={(record.actualPaymentAmount - (record.successRefundAmount + record.waitRefundAmount)) <= 0 ? true : false}
              />
            }
          </>
        }
      }
    ];

    useEffect(() => {
      if(paymentRecord) {
        execute(getStudentPaymentsDetailsTodo(paymentRecord.paymentRecordCode));
      }
    }, [paymentRecord]);  

    refundAmounts.forEach((value) => {
      if(value > 0) refundTotalAmount += value;
    });

    return(
      <>
        <Modal
          title={t('student.student.button28')}
          width={900}
          open={visible}
          onCancel={() => setVisible(false)}
          footer={[
              <div className='comm-btn__area justify-content-center'>
                <AntButton
                    key="submit"
                    className='btn-basic pink'
                    onClick={onOpenConfirm}
                >
                    {t('class.class.button8')}
                </AntButton>
                <AntButton
                    key="cancel"
                    className='btn-basic gray'
                    onClick={() => setVisible(false)}
                >
                    {t('common.button.cancel')}
                </AntButton>
              </div>
          ]}
        >
            * 정산 버튼 클릭 시 환불탭 &gt; 환불 대상에서 환불 진행 가능 합니다.<br/>
            * 환불금액을 0으로 입력 시 환불 정산에서 제외됩니다. (금액이 0인 경우 재결제 여부 체크 또한 제외됩니다)<br/>
            * '재결제 여부' 체크 시 다시 수납할 수 있도록 납입 대상에 추가됩니다. (수강료, 셔틀비, 온라인, ALEX)
            <Table
              className='comm-table__area'
              columns={columns}
              dataSource={res?.data}
              pagination={{ pageSize: 99999 }}
            />

            <div className="flex">
              <div style={{width:'80%'}} ></div>
              <div className="table-mini w-210p" >
                <Descriptions 
                  column={1}
                  size="small"
                  bordered
                >
                  <Descriptions.Item className="text-center" label={t('student.student.column86')}>
                    {
                      refundTotalAmount
                    }
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </div>
        </Modal>
        
        {confirmOpenVisible && (
        <PartialRefundConfirmModal
          visible={confirmOpenVisible}
          setVisible={setConfirmOpenVisible}
          onConfirm={onSubmit}
          titleText={t('student.student.button28')}
          amount={refundTotalAmount.toString()}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      )}
      </>
    );
}

export default PartialRefundModal;